import { inject, Injectable, Injector, isDevMode } from '@angular/core';
import { AppConfig } from '@app/config/config.type';
import { loadConfig } from '@cudirect/hybrid/loadConfig';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { environment } from 'environments/environment';
import { registerDevtoolsState } from '@shared/service/devtools/devtools-state-registry.service';

@Injectable({
  providedIn: 'root'
})
export class RuntimeConfigService {
  // Cached configuration object
  // This will be populated after the first loadConfig call
  private config: AppConfig;
  // Base URL components that will be constructed from the loaded configuration
  // The root URL for API requests
  private root: string;
  // The base path for API requests
  private basePath: string;
  private readonly injector = inject(Injector);

  private get isDevLike() {
    return isDevMode() || environment.e2e;
  }

  private get logger(): SawDust {
    return this.injector.get(SawDust);
  }

  constructor() {
    registerDevtoolsState(this, 'RuntimeConfigService');
  }

  /**
   * Returns the currently loaded runtime configuration object.
   *
   * @returns The cached config object, or undefined if not yet loaded.
   * @remarks
   * This is a synchronous getter. To ensure config is loaded, use {@link loadConfig} first.
   */
  public get getConfig(): AppConfig | undefined {
    return this.config;
  }

  /**
   * Returns the root URL for API requests.
   *
   * @returns The root URL string.
   */
  public get getRoot(): string {
    return this.root;
  }

  /**
   * Returns the suffix for API requests.
   *
   * @returns The base path string.
   */
  public get getBasePath(): string {
    return this.basePath;
  }

  /**
   * Loads the runtime configuration from a JSON file.
   *
   * @returns A promise that resolves with the loaded configuration object.
   * @remarks
   * This method fetches the configuration from `./assets/data/config.json` and caches it.
   * If the configuration is already loaded, it returns the cached version.
   */
  public async loadConfig(): Promise<AppConfig> {
    if (this.config) {
      return this.config;
    }

    this.config = (await loadConfig()) as AppConfig;

    return this.config;
  }

  /**
   * Returns the API base URL, loading config if necessary.
   *
   * @param apiRouteScope Optional override for the API route scope.
   * If not provided, it defaults to the external route scope set in the config.
   * @param apiVersion Optional override for the API version.
   * If not provided, it defaults to the v1 version set in the config.
   * @returns A promise that resolves to the API base URL string.
   */
  public getDXAPIBaseUrl(apiRouteScope?: string, apiVersion?: string): string {
    return this.generateDXBaseURL(apiRouteScope, apiVersion);
  }

  /**
   * Generates the base URL for API requests.
   * @param apiRouteScope Optional override for the API route scope.
   * @param apiVersion Optional override for the API version.
   * @returns The generated base URL string.
   */
  private generateDXBaseURL(apiRouteScope?: string, apiVersion?: string): string {
    const apiConfig = this.config?.consumerPortalApi;
    const browserOrigin = this.getBrowserOrigin();

    const normalize = (s?: string) => (s ? s.replace(/^\/+/, '').replace(/\/+$/, '') : '');
    const join = (root: string, path: string) => {
      const r = (root || '').replace(/\/+$/, '');
      const p = normalize(path);
      return p ? (r ? `${r}/${p}` : p) : r;
    };
    const isLocalHost = (host: string) => {
      const h = (host || '').toLowerCase();
      return h.includes('localhost') || h === '127.0.0.1' || h === '::1';
    };
    const makeRoot = (domain?: string, isHTTPS?: boolean, port?: string | number): string => {
      const raw = (domain ?? '').trim();
      if (!raw) return '';
      if (/^https?:\/\//i.test(raw)) {
        try {
          return new URL(raw).origin;
        } catch {
          /* fall through */
        }
      }
      const proto = isHTTPS ? 'https://' : 'http://';
      const hasPortInDomain = /:\d+$/.test(raw);
      const portPart = !hasPortInDomain && port !== undefined && String(port) !== '' ? `:${port}` : '';
      return `${proto}${raw}${portPart}`;
    };

    if (!apiConfig) {
      if (this.isDevLike && browserOrigin) {
        this.logger.warn('Runtime config missing consumerPortalApi; using browser origin for dev.');
        this.root = browserOrigin;
        this.basePath = '';
        return this.root;
      }
      this.logger.error('consumerPortalApi missing; cannot compute DX base URL.');
      this.root = '';
      this.basePath = '';
      return '';
    }

    const { isHTTPS, port, domain, suffix, api } = apiConfig;
    this.root = makeRoot(domain, isHTTPS, port) || (this.isDevLike ? (browserOrigin ?? '') : '');

    if (!this.root) {
      this.basePath = '';
      return '';
    }

    if (domain && isLocalHost(String(domain))) {
      const updatedSuffix = 'cpapi/api';
      this.basePath = updatedSuffix;
      return join(this.root, updatedSuffix);
    }

    const routeScope = normalize(apiRouteScope ?? apiConfig.routeScope);
    const version = normalize(apiVersion ?? apiConfig.version);
    const normalizedSuffix = normalize(suffix);
    const normalizedApi = normalize(api);

    if (!routeScope || !version || !normalizedApi) {
      this.logger.warn('Configuration is missing required fields: routeScope, version, or api.');
      const fallbackPath = normalizedSuffix ?? '';
      this.basePath = fallbackPath;
      return join(this.root, fallbackPath);
    }

    const segments = [routeScope, normalizedSuffix, version, normalizedApi].filter(Boolean) as string[];
    this.basePath = segments.join('/');
    return join(this.root, this.basePath);
  }

  private normalizePath(fragment?: string): string {
    if (!fragment) {
      return '';
    }
    return fragment.replace(/^\/+/, '').replace(/\/+$/, '');
  }

  private getBrowserOrigin(): string | null {
    if (typeof window === 'undefined' || !window?.location) {
      return null;
    }

    try {
      return window.location.origin;
    } catch {
      return null;
    }
  }
}
